'use client';

import { useEffect, useRef, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { cn } from '@/lib/utils';
import { useAddressStore } from '@/stores/address-store';

// Types for Yandex Maps
interface YMaps {
  Map: new (container: HTMLElement, options: YMapOptions) => YMap;
  Placemark: new (coordinates: number[], properties: object, options: object) => YPlacemark;
  Polygon: new (coordinates: number[][][], properties: object, options: object) => YPolygon;
  ready: (callback: () => void) => void;
}

interface YMapOptions {
  center: number[];
  zoom: number;
  controls?: string[];
  behaviors?: string[];
}

interface YMap {
  events: {
    add: (event: string, handler: (e: unknown) => void) => void;
  };
  options: {
    set: (key: string, value: unknown) => void;
    get: (key: string) => unknown;
  };
  geoObjects: {
    add: (object: YPlacemark | YPolygon) => void;
    removeAll: () => void;
  };
  setCenter: (center: number[], zoom?: number, options?: object) => void;
  destroy: () => void;
  // Convert pixel coords to geo coords
  converter?: {
    pageToGeo: (point: number[]) => number[] | null;
  };
  // Alternative: get bounds and calculate
  getBounds: () => number[][];
  getZoom: () => number;
  getCenter: () => number[];
  container: {
    getSize: () => number[];
  };
}

interface YPlacemark {
  geometry: {
    setCoordinates: (coordinates: number[]) => void;
  };
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface YPolygon {}

declare global {
  interface Window {
    ymaps?: YMaps;
  }
}

export interface MapClickResult {
  lat: number;
  lon: number;
  address: string;
}

export interface YandexMapHandle {
  pixelToGeo: (pageX: number, pageY: number) => { lat: number; lon: number } | null;
  placeMarker: (lat: number, lon: number) => void;
}

interface YandexMapProps {
  coordinates?: { lat: number; lon: number };
  zoom?: number;
  className?: string;
  showZones?: boolean;
}

const DEFAULT_CENTER: [number, number] = [47.2357, 39.7015];
const DEFAULT_ZOOM = 12;
const YANDEX_API_KEY = process.env.NEXT_PUBLIC_YANDEX_MAPS_API_KEY || '';

export const YandexMap = forwardRef<YandexMapHandle, YandexMapProps>(function YandexMap(
  { coordinates, zoom = DEFAULT_ZOOM, className, showZones = false },
  ref
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<YMap | null>(null);
  const markerRef = useRef<YPlacemark | null>(null);
  const ymapsRef = useRef<YMaps | null>(null);
  const [ymapsReady, setYmapsReady] = useState(false);

  const deliveryPoints = useAddressStore((state) => state.deliveryPoints);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    pixelToGeo(pageX: number, pageY: number) {
      if (!mapRef.current || !containerRef.current) return null;

      const rect = containerRef.current.getBoundingClientRect();
      const x = pageX - rect.left;
      const y = pageY - rect.top;

      // Use map bounds to convert pixel → geo
      const map = mapRef.current;
      const bounds = map.getBounds(); // [[latMin, lonMin], [latMax, lonMax]]
      const size = [rect.width, rect.height];

      const lat = bounds[1][0] - (y / size[1]) * (bounds[1][0] - bounds[0][0]);
      const lon = bounds[0][1] + (x / size[0]) * (bounds[1][1] - bounds[0][1]);

      return { lat, lon };
    },
    placeMarker(lat: number, lon: number) {
      const ymaps = ymapsRef.current;
      if (!ymaps || !mapRef.current) return;

      const coords = [lat, lon];
      try {
        if (markerRef.current) {
          markerRef.current.geometry.setCoordinates(coords);
        } else {
          markerRef.current = new ymaps.Placemark(coords, {}, {
            preset: 'islands#violetDotIcon',
            iconColor: '#7B42FF',
          });
          mapRef.current.geoObjects.add(markerRef.current);
        }
        mapRef.current.setCenter(coords, undefined, { duration: 300 });
      } catch { /* ignore */ }
    },
  }), [ymapsReady]);

  // Load Yandex Maps script
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onReady = () => {
      ymapsRef.current = window.ymaps!;
      setYmapsReady(true);
    };

    if (window.ymaps) {
      window.ymaps.ready(onReady);
      return;
    }

    const existingScript = document.querySelector('script[src*="api-maps.yandex.ru"]');
    if (existingScript) {
      const checkReady = setInterval(() => {
        if (window.ymaps) {
          window.ymaps.ready(() => { onReady(); clearInterval(checkReady); });
        }
      }, 100);
      return () => clearInterval(checkReady);
    }

    const script = document.createElement('script');
    script.src = `https://api-maps.yandex.ru/2.1/?apikey=${YANDEX_API_KEY}&lang=ru_RU`;
    script.async = true;
    script.onload = () => { window.ymaps?.ready(onReady); };
    document.head.appendChild(script);
  }, []);

  // Create map ONCE
  useEffect(() => {
    if (!ymapsReady || !containerRef.current) return;
    const ymaps = ymapsRef.current!;

    if (mapRef.current) {
      try { mapRef.current.destroy(); } catch { /* ignore */ }
      mapRef.current = null;
      markerRef.current = null;
    }

    try {
      mapRef.current = new ymaps.Map(containerRef.current, {
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        controls: ['zoomControl'],
      });
    } catch (error) {
      console.error('Failed to initialize Yandex Map:', error);
    }

    return () => {
      if (mapRef.current) {
        try { mapRef.current.destroy(); } catch { /* ignore */ }
        mapRef.current = null;
        markerRef.current = null;
      }
    };
  }, [ymapsReady]);

  // Draw delivery zones
  useEffect(() => {
    const ymaps = ymapsRef.current;
    if (!ymaps || !mapRef.current || !showZones || deliveryPoints.length === 0) return;

    deliveryPoints.forEach((point) => {
      point.zones.forEach((zone) => {
        if (zone.polygon && mapRef.current) {
          const convertedPolygon = zone.polygon.map((ring) =>
            ring.map((coord) => {
              if (Array.isArray(coord) && coord.length === 2) {
                return [coord[1], coord[0]];
              }
              return coord;
            })
          );
          const polygon = new ymaps.Polygon(
            convertedPolygon,
            { hintContent: `Доставка: ${zone.delivery_cost}₽` },
            // 0 ₽ — фиолетовый (как было), платная зона — розовый
            {
              fillColor: Number(zone.delivery_cost) > 0 ? '#EC489940' : '#7B42FF55',
              strokeColor: Number(zone.delivery_cost) > 0 ? '#EC4899' : '#7B42FF',
              strokeWidth: 1,
              strokeOpacity: 0.6,
            }
          );
          mapRef.current!.geoObjects.add(polygon);
        }
      });
    });
  }, [ymapsReady, showZones, deliveryPoints]);

  // Update marker and center when coordinates change
  useEffect(() => {
    const ymaps = ymapsRef.current;
    if (!ymaps || !mapRef.current || !coordinates) return;

    const newCenter = [coordinates.lat, coordinates.lon];
    try {
      mapRef.current.setCenter(newCenter, zoom, { duration: 300 });
      if (markerRef.current) {
        markerRef.current.geometry.setCoordinates(newCenter);
      } else {
        markerRef.current = new ymaps.Placemark(newCenter, {}, {
          preset: 'islands#violetDotIcon',
          iconColor: '#7B42FF',
        });
        mapRef.current.geoObjects.add(markerRef.current);
      }
    } catch (error) {
      console.error('Failed to update map:', error);
    }
  }, [coordinates, zoom, ymapsReady]);

  return (
    <div
      ref={containerRef}
      className={cn(
        'w-full h-full min-h-[200px] rounded-card overflow-hidden',
        'bg-[var(--surface-secondary)]',
        className
      )}
    />
  );
});
